import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Bus, MapPin, Clock, Navigation, RefreshCw, Wifi, WifiOff } from "lucide-react";

const REFRESH_MS = 20000;

export default function ParentTripPage() {
  const { t } = useI18n();
  const { tripId } = useParams();
  const [trip, setTrip] = useState(null);
  const [live, setLive] = useState(null);
  const [mapsKey, setMapsKey] = useState(null);
  const timer = useRef(null);

  const fetchTrip = useCallback(async () => {
    try {
      // Get trip via list (filter); fallback: nothing
      const today = new Date().toISOString().slice(0, 10);
      const r = await api.get(`/trips?date=${today}`);
      const found = (r.data || []).find(x => x.id === tripId);
      setTrip(found || null);
      if (found?.route_id) {
        const lr = await api.get(`/routes/${found.route_id}/live`);
        setLive(lr.data);
      }
    } catch (e) { console.warn("fetchTrip failed:", e?.message || e); }
  }, [tripId]);

  useEffect(() => {
    api.get("/settings/public").then(r => setMapsKey(r.data?.google_maps_api_key || null)).catch(() => {});
    fetchTrip();
    timer.current = setInterval(fetchTrip, REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [fetchTrip]);

  const route = live?.stops ? { stops: live.stops, name: live?.route_name } : trip?.route;
  const location = live?.location;
  const fresh = location?.updated_at && (Date.now() - new Date(location.updated_at).getTime() < 5 * 60_000);

  const buildEmbed = () => {
    if (!mapsKey || !location?.lat || !location?.lng) return null;
    const stops = route?.stops || [];
    if (stops.length === 0) return null;
    const last = stops[stops.length - 1];
    if (!last.lat || !last.lng) return null;
    const wp = stops.slice(0, -1).filter(s => s.lat && s.lng).map(s => `${s.lat},${s.lng}`).join("|");
    let url = `https://www.google.com/maps/embed/v1/directions?key=${mapsKey}&origin=${location.lat},${location.lng}&destination=${last.lat},${last.lng}&mode=driving`;
    if (wp) url += `&waypoints=${encodeURIComponent(wp)}`;
    return url;
  };
  const embedUrl = buildEmbed();
  const fallbackDirections = location && route?.stops?.length
    ? `https://www.google.com/maps/dir/?api=1&origin=${location.lat},${location.lng}&destination=${route.stops[route.stops.length - 1].lat},${route.stops[route.stops.length - 1].lng}`
    : null;

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <Link to="/" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-4 h-4 lucide-rotate-rtl" /> {t("overview")}</Link>
        <Button variant="ghost" size="icon" onClick={fetchTrip} data-testid="parent-trip-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <Card className="p-5 border shadow-none">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center"><Bus className="w-6 h-6" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-lg truncate">{route?.name || trip?.route_id}</div>
            <div className="text-xs text-muted-foreground">{trip ? `${t(trip.direction)} · ${trip.date}` : "—"}</div>
          </div>
          {trip && (
            <span className={`text-xs px-2 py-1 rounded-full ${trip.status === "completed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : trip.status === "started" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" : "bg-muted text-muted-foreground"}`}>
              {t(trip.status)}
            </span>
          )}
        </div>
      </Card>

      {/* Live map */}
      {embedUrl ? (
        <Card className="overflow-hidden border shadow-none">
          <iframe
            title="Live bus map"
            src={embedUrl}
            className="w-full h-72 sm:h-96"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="parent-trip-embed"
          />
        </Card>
      ) : location ? (
        <Card className="p-5 border shadow-none border-dashed">
          <div className="text-sm flex items-center gap-2 mb-2 text-muted-foreground">
            <MapPin className="w-4 h-4" /> {t("address")}: <span className="font-mono">{location.lat?.toFixed(4)}, {location.lng?.toFixed(4)}</span>
          </div>
          {fallbackDirections && (
            <a href={fallbackDirections} target="_blank" rel="noreferrer" className="text-sm text-primary underline inline-flex items-center gap-1" data-testid="parent-trip-open-maps">
              <Navigation className="w-3.5 h-3.5" /> {t("open_map")}
            </a>
          )}
          {!mapsKey && <p className="text-xs text-muted-foreground mt-2">Embedded map disabled — admin needs to set a Google Maps API key in /settings → Maps.</p>}
        </Card>
      ) : (
        <Card className="p-8 text-center text-muted-foreground border-dashed">
          <WifiOff className="w-6 h-6 mx-auto mb-2 opacity-50" />
          <div className="text-sm">{t("no_data")}</div>
          <div className="text-xs mt-1">{t("driver")} {t("status")}: offline</div>
        </Card>
      )}

      {/* Live status */}
      {location && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {fresh ? <Wifi className="w-3.5 h-3.5 text-emerald-500" /> : <WifiOff className="w-3.5 h-3.5 text-amber-500" />}
          <span>{fresh ? t("active") : t("inactive")}</span>
          <span>· {new Date(location.updated_at).toLocaleTimeString()}</span>
          {location.speed != null && <span>· {Math.round(location.speed)} km/h</span>}
        </div>
      )}

      {/* ETA per stop */}
      {live?.eta && live.eta.length > 0 && (
        <Card className="p-4 border shadow-none">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> ETA — {t("stops")}</h3>
          <ul className="space-y-2">
            {live.eta.map((s, i) => (
              <li key={s.id || s.stop_id || `${s.name}-${i}`} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b border-border/40 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary grid place-items-center text-[10px] font-semibold">{i + 1}</span>
                  <span>{s.name}</span>
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {s.eta_min != null ? `${s.eta_min} min` : "—"}
                  {s.distance_km != null && <span> · {s.distance_km} km</span>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
